"use client";

import Image from "next/image";
import { useEffect, useState, useRef } from "react";
import { useInView } from "@/hooks/useInView";
import EnrollTourButtons from "@/components/cms/EnrollTourButtons";
import { motion } from "framer-motion";
import { useParallaxOffset } from "@/hooks/useParallaxOffset";



export default function ExtraHero({ cms, logoUrl }: { cms?: Record<string, string>; logoUrl?: string | null }) {
  const parallaxRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  
  // Use optimized parallax hook (768px instead of 640px for this component)
  const [offset, isDesktop, transform3d] = useParallaxOffset(0.2, 768, parallaxRef);
  
  // Handle hydration
  useEffect(() => {
    setMounted(true);
  }, []);

  const headingRef = useRef<HTMLDivElement>(null);
  const showHeading = useInView(headingRef, {
    margin: "0px",
  });

  return (
    <section className="relative min-h-screen flex items-center justify-center font-nunito overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div
          ref={parallaxRef}
          className="will-change-transform absolute w-full -top-[10%] h-[120%]"
          style={mounted && isDesktop ? { transform: transform3d } : { transform: 'translate3d(0, 0, 0)' }}
        >
          {cms?.bg_image_url && <Image
            src={cms.bg_image_url}
            alt="Students in classroom"
            fill
            className="object-cover"
            priority
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 1920px"
          />}
        </div>
        <div className="absolute inset-0" style={{ background: cms?.overlay_color ?? "rgba(6,22,82,0.6)" }}></div>
      </div>
      <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto flex flex-col items-center pt-12 pb-12 sm:pt-24 sm:pb-24">
        {logoUrl && (
        <div className="mb-8 pt-8">
          <Image
            src={logoUrl}
            alt="Ashton John's Private School Logo"
            width={200}
            height={200}
            className="mx-auto w-52 h-48"
            priority
          />
        </div>
        )}
        <motion.div
          ref={headingRef}
          initial={{ opacity: 0, y: 60 }}
          animate={showHeading ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
          transition={{ type: "spring", stiffness: 120, damping: 20 }}
          className="space-y-4 mb-12"
        >
          <h1 className="text-2xl md:text-3xl font-light mb-8">
            {cms?.heading /* || "At Ashton Johns we believe that student development in sport and extra-curricular activities are also important." */}
          </h1>
          <h2 className="text-3xl md:text-4xl font-bold mb-8">
            {cms?.subheading /* || "All our students are encouraged to participate in the variety of activities we offer." */}
          </h2>
        </motion.div>
        <EnrollTourButtons
          className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12"
          buttonClassName="bg-school-gold hover:bg-school-gold-dark text-black font-bold py-4 px-8 rounded text-lg transition-colors text-center"
          enrollText={cms?.enrol_btn_text /* || "ENROL NOW" */}
          tourText={cms?.tour_btn_text /* || "BOOK A TOUR" */}
          enrollUrl={cms?.enrol_btn_url}
          tourUrl={cms?.tour_btn_url}
        />
      </div>
    </section>
  );
}
